const COMPRESSIBLE_IMAGE_TYPES = new Set(["image/jpeg", "image/jpg", "image/png", "image/webp"])

const DEFAULT_MAX_WIDTH = 1920
const DEFAULT_MAX_HEIGHT = 1920
const DEFAULT_TARGET_BYTES = 1_200_000
const DEFAULT_MIN_SOURCE_BYTES = 1_000_000

type CompressionOptions = {
  maxWidth?: number
  maxHeight?: number
  targetBytes?: number
  minSourceBytes?: number
}

function getOutputMimeType(file: File): string | null {
  if (!COMPRESSIBLE_IMAGE_TYPES.has(file.type)) {
    return null
  }

  if (file.type === "image/png" || file.type === "image/webp") {
    return "image/webp"
  }

  return "image/jpeg"
}

function replaceFileExtension(fileName: string, mimeType: string): string {
  const dotIndex = fileName.lastIndexOf(".")
  const baseName = dotIndex >= 0 ? fileName.slice(0, dotIndex) : fileName

  if (mimeType === "image/webp") {
    return `${baseName}.webp`
  }

  return `${baseName}.jpg`
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    const objectUrl = URL.createObjectURL(file)

    image.onload = () => {
      URL.revokeObjectURL(objectUrl)
      resolve(image)
    }

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error("Failed to load image for compression"))
    }

    image.src = objectUrl
  })
}

function canvasToBlob(canvas: HTMLCanvasElement, mimeType: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Failed to encode compressed image"))
        return
      }

      resolve(blob)
    }, mimeType, quality)
  })
}

export async function compressImageForUpload(file: File, options: CompressionOptions = {}): Promise<File> {
  const mimeType = getOutputMimeType(file)
  if (!mimeType) {
    return file
  }

  const targetBytes = options.targetBytes ?? DEFAULT_TARGET_BYTES
  const minSourceBytes = options.minSourceBytes ?? DEFAULT_MIN_SOURCE_BYTES
  if (file.size <= minSourceBytes) {
    return file
  }

  const maxWidth = options.maxWidth ?? DEFAULT_MAX_WIDTH
  const maxHeight = options.maxHeight ?? DEFAULT_MAX_HEIGHT
  const image = await loadImage(file)

  const widthScale = maxWidth / image.width
  const heightScale = maxHeight / image.height
  const scale = Math.min(1, widthScale, heightScale)

  const canvas = document.createElement("canvas")
  canvas.width = Math.max(1, Math.round(image.width * scale))
  canvas.height = Math.max(1, Math.round(image.height * scale))

  const context = canvas.getContext("2d")
  if (!context) {
    return file
  }

  context.drawImage(image, 0, 0, canvas.width, canvas.height)

  const qualitySteps = mimeType === "image/webp" ? [0.86, 0.78, 0.7, 0.62] : [0.82, 0.74, 0.66, 0.58]

  let bestBlob: Blob | null = null
  for (const quality of qualitySteps) {
    const blob = await canvasToBlob(canvas, mimeType, quality)
    bestBlob = blob

    if (blob.size <= targetBytes) {
      break
    }
  }

  if (!bestBlob) {
    return file
  }

  if (bestBlob.size >= file.size * 0.95) {
    return file
  }

  return new File([bestBlob], replaceFileExtension(file.name, mimeType), {
    type: mimeType,
    lastModified: file.lastModified,
  })
}
